import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import InterviewRequestForm from "./InterviewRequestForm";
import featureFlagService, {
  FEATURES,
} from "../../services/featureFlagService";
import { isExternalListing } from "../../utils/listingSource";
import { useSwipeQueue } from "../../contexts/SwipeQueueContext";
import useListingReview from '../Profile/useListingReview';

const InterviewRequestContext = createContext(null);

/**
 * useInterestExpression
 *
 * Returns { isEnabled, open }. Components call open(listing) when the
 * provider tries to express interest. If they haven't viewed this listing's
 * back during the current visit, they complete the listing review first.
 * If the Flagsmith flag is off, open then resolves with `{ skipForm: true }`
 * and the caller saves the legacy swipe. If the flag is on, open shows the
 * two-step modal and resolves with:
 *
 *   - null                              the provider cancelled (X / Esc /
 *                                       backdrop). No Swipe doc exists.
 *   - { skipForm: true }                the flag is off (legacy path).
 *   - { swipeId, interviewRequested }   a Swipe now exists. interviewRequested
 *                                       is true for the interview path,
 *                                       false for "Not right now, just
 *                                       connect us".
 */
export function useInterestExpression() {
  const ctx = useContext(InterviewRequestContext);
  if (!ctx) {
    throw new Error(
      "useInterestExpression must be used inside <InterviewRequestProvider>",
    );
  }
  return ctx;
}

/* -------------------------------------------------------------------------- */
/* Listing-context helpers                                                    */
/* -------------------------------------------------------------------------- */

function listingTitleOf(listing) {
  return listing?.title || listing?.jobTitle || listing?.name || null;
}

function facilityNameOf(listing) {
  return (
    listing?.facility?.name ||
    listing?.facilityName ||
    listing?.groupName ||
    null
  );
}

function providerRoleOf(listing) {
  return listing?.providerType || listing?.role || listing?.discipline || null;
}

/** Format a listing's compensation as a short header label, e.g. "$250/hr". */
function rateLabelOf(listing) {
  const amount = listing?.payAmount;
  if (!amount || typeof amount !== "number") return null;
  const period = listing?.payPeriod === "annual" ? "/yr" : "/hr";
  return `$${Math.round(amount).toLocaleString("en-US")}${period}`;
}

/** Format a listing's location as a short header label, e.g. "Worcester, MA". */
function locationLabelOf(listing) {
  const loc = listing?.location || listing?.facility?.location;
  if (typeof loc === "string" && loc.trim()) return loc.trim();
  const city = listing?.city || listing?.facility?.city;
  const state = listing?.state || listing?.facility?.state;
  if (city && state) return `${city}, ${state}`;
  return state || city || null;
}

/**
 * InterviewRequestProvider
 *
 * Mounts the two-step InterviewRequestForm at the app root so any descendant
 * can open it through useInterestExpression. Resolves a promise per open()
 * call. The form reports which action was taken via onSubmit({ mode, slots,
 * questions }); this provider routes:
 *
 *   - mode 'interview' -> durable provider-interview-request outbox action
 *   - mode 'connect'   -> durable provider-connect outbox action
 *
 * The outbox selects the exact backend endpoint and preserves the complete
 * JSON payload across reload/offline. This provider closes only after a server
 * acknowledgement and normalizes it to { swipeId, interviewRequested }.
 */
export function InterviewRequestProvider({ children }) {
  const [pending, setPending] = useState(null);
  const resolverRef = useRef(null);
  const pendingActionRef = useRef(null);
  const { enqueueSwipe } = useSwipeQueue();
  const { requestReview, reviewDialog } = useListingReview();
  const openingRef = useRef(false);

  const open = useCallback(async (listing) => {
    if (openingRef.current || resolverRef.current) return null;
    openingRef.current = true;
    const reviewed = await requestReview(listing);
    openingRef.current = false;
    if (!reviewed) return null;
    if (
      !featureFlagService.isFeatureEnabled(
        FEATURES.INTERVIEW_REQUEST_FORM_ENABLED,
      )
    ) {
      return Promise.resolve({ skipForm: true });
    }
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setPending({ listing });
    });
  }, [requestReview]);

  const resolveAndClose = useCallback((result) => {
    const resolver = resolverRef.current;
    resolverRef.current = null;
    pendingActionRef.current = null;
    setPending(null);
    if (resolver) resolver(result);
  }, []);

  // A first attempt may report offline while its durable row remains queued.
  // If the browser comes back online without a reload, close the still-open
  // form only when that exact queued action later receives a server ack.
  useEffect(() => {
    const handleAcknowledged = (event) => {
      const expected = pendingActionRef.current;
      const item = event?.detail?.item;
      if (
        !expected?.clientActionId ||
        item?.clientActionId !== expected.clientActionId
      ) {
        return;
      }
      const response = event.detail.result;
      const swipeId =
        response?.swipeId || response?._id || response?.swipe?._id || null;
      resolveAndClose({
        swipeId,
        interviewRequested: expected.mode === "interview",
      });
    };
    window.addEventListener(
      "rxrooster:swipeQueueAcknowledged",
      handleAcknowledged,
    );
    return () =>
      window.removeEventListener(
        "rxrooster:swipeQueueAcknowledged",
        handleAcknowledged,
      );
  }, [resolveAndClose]);

  // The form's single submit callback. mode decides the endpoint; both
  // responses normalize to { swipeId, interviewRequested }. Errors bubble
  // back to the form so it can surface them and stay open for a retry.
  const handleSubmit = useCallback(
    async ({ mode, slots, questions, introConsent }) => {
      const listing = pending?.listing;
      if (!listing) return;
      let queued;
      if (mode === "interview") {
        queued = await enqueueSwipe({
          type: "provider-interview-request",
          payload: {
            listingId: listing._id,
            slots,
            questions: questions || [],
            // open() awaited the fit confirmation before this form appeared.
            fitAcknowledged: true,
            ...(listing.sourceInterestSwipeId
              ? { sourceInterestSwipeId: listing.sourceInterestSwipeId }
              : {}),
            // External listings only: the wizard sets this to true when the
            // provider taps "Send request & introduce me". The backend stamps
            // externalIntroConsentAt and opens the PII gate for the direct intro.
            ...(typeof introConsent === "boolean" ? { introConsent } : {}),
          },
        });
      } else {
        queued = await enqueueSwipe({
          type: listing.sourceInterestSwipeId
            ? "provider-inbound-response"
            : "provider-connect",
          payload: {
            listingId: listing._id,
            openingQuestions: questions || [],
            fitAcknowledged: true,
            ...(listing.sourceInterestSwipeId
              ? {
                  sourceInterestSwipeId: listing.sourceInterestSwipeId,
                  direction: "right",
                }
              : {}),
          },
        });
      }
      if (!queued?.success) {
        if (queued?.terminal) {
          // The server refused this request for good and the outbox dropped it,
          // so no later acknowledgement will arrive. Keep the form open with a
          // clear message; slot_in_past carries its own copy.
          throw queued.userMessage
            ? new Error(queued.userMessage)
            : queued.error ||
                new Error("The server did not accept this interview request");
        }
        if (queued?.item?.clientActionId) {
          pendingActionRef.current = {
            clientActionId: queued.item.clientActionId,
            mode,
          };
        }
        throw (
          queued?.error ||
          new Error("The server did not acknowledge this action")
        );
      }
      const response = queued.result;
      const swipeId =
        response?.swipeId || response?._id || response?.swipe?._id || null;
      resolveAndClose({ swipeId, interviewRequested: mode === "interview" });
    },
    [enqueueSwipe, pending, resolveAndClose],
  );

  const handleCancel = useCallback(() => {
    resolveAndClose(null);
  }, [resolveAndClose]);

  const value = useMemo(
    () => ({
      isEnabled: featureFlagService.isFeatureEnabled(
        FEATURES.INTERVIEW_REQUEST_FORM_ENABLED,
      ),
      open,
      review: requestReview,
    }),
    [open, requestReview],
  );

  const listing = pending?.listing;

  return (
    <InterviewRequestContext.Provider value={value}>
      {children}
      {reviewDialog}
      <InterviewRequestForm
        isOpen={!!pending}
        listingTitle={listingTitleOf(listing)}
        facilityName={facilityNameOf(listing)}
        providerRole={providerRoleOf(listing)}
        rateLabel={rateLabelOf(listing)}
        locationLabel={locationLabelOf(listing)}
        requireIntroConsent={isExternalListing(listing)}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
      />
    </InterviewRequestContext.Provider>
  );
}

InterviewRequestProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default InterviewRequestProvider;
